// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, IconButton, Button } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseLabel, BasePieChart, BaseStatus, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { IQueryParamListExport, IQuoteRevenueReport, IQuoteRevenueReportData } from '~/utils/interface/reports';
import { IBasePieData, IHeaderCellType } from '~/utils/interface/common';
import { capitalizeWords, formatCurrencyUSD, formatDate, formatNumberShort, getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { exportDataReport, getListQuoteRevenueReport } from '~/thunks/reports/reportsThunk';
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { DateFormatEnum, QuoteStatusEnum, ReportTypeEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './QuoteRevenueReport.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IQuoteRevenueReport>[] => {
  return [
    {
      id: 'quoteId',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_quote_id')}</Box>
        </Stack>
      ),
      render: (record: IQuoteRevenueReport) => <Typography className={cx('quoteId')}>{record?.id}</Typography>,
    },
    {
      id: 'total',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_total')}</Box>
        </Stack>
      ),
      render: (record: IQuoteRevenueReport) => (
        <Typography className={cx('total')}>
          {formatCurrencyUSD({ value: record?.total, showDecimal: true })}
        </Typography>
      ),
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_client')}</Box>
        </Stack>
      ),
      render: (record: IQuoteRevenueReport) => (
        <Typography className={cx('textValue')}>
          {getFullName({ firstName: record?.client?.firstName, lastName: record?.client?.lastName })}
        </Typography>
      ),
    },
    {
      id: 'manager',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_managed_by')}</Box>
        </Stack>
      ),
      render: (record: IQuoteRevenueReport) => (
        <Typography className={cx('textValue')}>
          {getFullName({ firstName: record?.managedBy?.firstName, lastName: record?.managedBy?.lastName })}
        </Typography>
      ),
    },
    {
      id: 'email',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_client_email')}</Box>
        </Stack>
      ),
      render: (record: IQuoteRevenueReport) => (
        <Typography className={cx('textValue')}>{record?.client?.clientContacts?.[0]?.email}</Typography>
      ),
    },
    {
      id: 'address',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_client_address')}</Box>
        </Stack>
      ),
      render: (record: IQuoteRevenueReport) => (
        <Typography className={cx('textValue')}>{record?.clientAddress?.address}</Typography>
      ),
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_status')}</Box>
        </Stack>
      ),
      render: (record: IQuoteRevenueReport) =>
        record?.status ? <BaseStatus label={record?.status || EMPTY_STRING} /> : EMPTY_STRING,
    },
    {
      id: 'date',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_date')}</Box>
        </Stack>
      ),
      render: (record: IQuoteRevenueReport) => {
        return <Typography className={cx('textValue')}>{formatDate(record?.date, DateFormatEnum.M_D_YYYY)}</Typography>;
      },
    },
  ];
};

const QuoteRevenueReport = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [quoteRevenueReport, setQuoteRevenueReport] = useState<IQuoteRevenueReportData>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
  });

  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListReport();
  }, [paramsObject]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListReport = () => {
    setIsLoading(true);

    dispatch(getListQuoteRevenueReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses, pagination, revenueByQuoteStatus } = res.data;

        setQuoteRevenueReport({
          list: responses,
          statistic: revenueByQuoteStatus,
        });

        setPagination(pagination);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListReport();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  // const getTotalRevenue = () => {
  //   return clientIncomeReportList.reduce((sum, e) => sum + Number(e.revenue), 0);
  // };

  const convertPieChartData: IBasePieData[] = (() => {
    const statusColors: Record<string, string> = {
      [QuoteStatusEnum.QUOTE_REQUEST]: '#2D80FC',
      [QuoteStatusEnum.IN_PROCESS]: '#FEA500',
      [QuoteStatusEnum.COMPLETED]: '#77B900',
      [QuoteStatusEnum.PENDING_APPROVAL]: '#F86B38',
      [QuoteStatusEnum.QUOTE_EXPIRED]: '#C11574',
      [QuoteStatusEnum.QUOTE_CANCELLED]: '#DC2626',
      [QuoteStatusEnum.ACCEPTED]: '#34D399',
      [QuoteStatusEnum.REJECTED]: '#F87171',
    };

    if (!quoteRevenueReport?.statistic) return [];

    const result: IBasePieData[] = quoteRevenueReport.statistic.map((item) => {
      const revenue = Number(item.revenue || 0);

      return {
        id: capitalizeWords(item.status),
        label: formatNumberShort(revenue),
        value: revenue,
        color: statusColors[item.status.toLowerCase()],
      };
    });

    return result;
  })();

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamsObject({ ...paramsObject, page });
    setSearchParams({ ...params, page: String(page) });
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.QUOTE_REVENUE,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };
  //#endregion Handle Function

  return (
    <Stack id='quoteRevenueReport' className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center' justifyContent={'space-between'}>
        <Stack direction='row' spacing={2} alignItems='center'>
          <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
            <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
          </IconButton>

          <Stack direction={'column'} spacing={2}>
            <BaseLabel label={t('reports_quote_revenue_title')} styleType='bigTitle' />
            <Typography className={cx('description')}>{t('reports_quote_revenue_desc_page')}</Typography>
          </Stack>
        </Stack>

        <Button
          type='button'
          variant='contained'
          className={cx('buttonExport')}
          onClick={handleExport}
          disabled={!quoteRevenueReport?.list?.length}
          startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
        >
          {t('common_button_export_excel', { ns: 'common/base' })}
        </Button>
      </Stack>

      <Stack direction={'row'} className={cx('viewContent')}>
        <Stack direction={'column'} spacing={2}>
          <Stack direction={'column'} spacing={2}>
            <Typography className={cx('subTitle')}>{t('reports_quote_revenue_revenue_by_status')}</Typography>
          </Stack>

          <Stack direction={'row'} spacing={'80px'} alignItems={'center'}>
            <BasePieChart data={convertPieChartData.filter((item) => item.value > 0)} />

            <Stack spacing={3}>
              {convertPieChartData.map((item) => (
                <Stack key={item.id} direction='row' alignItems='center' spacing={2}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: item.color }} />
                  <Typography className={cx('labelStatus')}>{item.id}</Typography>
                  <Typography className={cx('valueStatus')}>
                    {formatCurrencyUSD({ value: item.value, showDecimal: true })}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <Box className={cx('tableContainer')}>
        <Typography className={cx('textTitleTable')}>{t('reports_quote_revenue_quote_details')}</Typography>

        <BaseTable
          dataSource={quoteRevenueReport?.list}
          columns={columns(t)}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          onPageChange={handlePageChange}
        />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default QuoteRevenueReport;
